/**
 * The membership plan a visitor chose BEFORE creating their account.
 *
 * Pricing comes first in the journey:
 *   choose plan → create account → Stripe/PayPal checkout → activate → dashboard
 *
 * The choice has to survive the whole hand-off, including browsers where
 * storage is unavailable (private windows, in-app browsers inside Instagram
 * or Slack, strict tracking-prevention settings). So it is carried three ways,
 * in order of trust:
 *
 *   1. the `?plan=` URL parameter on the next page — survives storage blocks
 *   2. localStorage / sessionStorage — survives reloads and new tabs
 *   3. an in-memory value — survives client-side navigation when 1 and 2 fail
 *
 * Only the plan *id* is ever carried. The Stripe Price and PayPal plan are
 * always resolved from the server-side allowlist, never from the browser, so a
 * tampered id cannot buy a cheaper plan — it is rejected server-side.
 */
import { getPlan, isKnownPlanId, type MembershipPlan } from "@/lib/membership-plans";

const KEY = "rt_selected_plan";

/** Last-resort carrier when both storages throw (private/in-app browsers). */
let memoryPlanId: string | null = null;

export function setSelectedPlanId(planId: string): void {
  if (!isKnownPlanId(planId)) return;
  memoryPlanId = planId;
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(KEY, planId);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(KEY, planId);
  } catch {
    /* ignore */
  }
}

/** Reads the stored id, ignoring anything that isn't a plan we sell. */
export function readSelectedPlanId(): string | null {
  if (typeof window === "undefined") return memoryPlanId;
  try {
    const v = localStorage.getItem(KEY);
    if (isKnownPlanId(v)) return v;
  } catch {
    /* ignore */
  }
  try {
    const v = sessionStorage.getItem(KEY);
    if (isKnownPlanId(v)) return v;
  } catch {
    /* ignore */
  }
  return memoryPlanId;
}

/**
 * Decide which plan id applies on a page that may have received `?plan=`.
 * Pure so the precedence rules are testable without a browser.
 *
 * The URL wins over storage: it is the fresher signal and it's the only one
 * that works when storage is blocked. Unknown ids on either side are ignored
 * rather than trusted, so a bad link falls back to the stored choice instead
 * of quietly switching the member onto another price.
 */
export function resolvePlanId(input: {
  paramPlanId?: string | null;
  storedPlanId?: string | null;
}): string | null {
  if (isKnownPlanId(input.paramPlanId)) return input.paramPlanId ?? null;
  if (isKnownPlanId(input.storedPlanId)) return input.storedPlanId ?? null;
  return null;
}

/**
 * Read the plan for the current page, preferring `?plan=` and re-persisting it
 * so later steps (and a reload) keep the same choice.
 */
export function adoptPlanFromUrl(paramPlanId?: string | null): string | null {
  const resolved = resolvePlanId({
    paramPlanId,
    storedPlanId: readSelectedPlanId(),
  });
  if (resolved) setSelectedPlanId(resolved);
  return resolved;
}

/** Always returns a plan — falls back to the monthly plan. */
export function readSelectedPlan(): MembershipPlan {
  return getPlan(readSelectedPlanId());
}

export function clearSelectedPlan(): void {
  memoryPlanId = null;
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
